import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import {Link} from 'react-router-dom';
import {KebabMenuIcon, ProjectIndicator, ProjectIndicatorList, Table, Dropdown, EconomicIcon, QuickbooksIcon, XeroIcon, Button} from 'web-components';
import Util from '../../../forecast-app/shared/util/util';
//import * as tracking from '../../../tracking';
import ForecastTooltip from '../../../forecast-app/shared/components/tooltips/ForecastTooltip';
import UpdateInvoiceMutation from '../../../mutations/update_invoice_mutation';
import {ProjectNameTooltip} from "../../../styles/components/project_name_tooltip";

export const InvoicesTable = ({theEyeOptions, data, totals, companyCurrency, onExportClick, handleOpenContextMenu, onEditInvoice, integrationSettings}) => {
	const intl = useIntl();

	const headerData = {
		rows: [
			{
				...totals,
			},
		],
	};

	const tableData = {
		rows: data,
	};
	const boolEyeOptions = useMemo(() => {
		const bools = {};
		theEyeOptions.forEach(eyeOption => {
			bools[eyeOption.name] = eyeOption.checked;
			if (eyeOption.nestedOptions) {
				eyeOption.nestedOptions.forEach(nestedOption => {
					bools[nestedOption.name] = nestedOption.checked;
				});
			}
		});
		return bools;
	}, [theEyeOptions]);

	const getIntegrationsContent = (invoiceId, status, xeroId, xeroTenantId, economicId, quickbooksId, quickbooksRealmId) => {
		const buttons = [];
		if (xeroId) {
			let xeroShortCode;
			if (integrationSettings && integrationSettings.xeroTenantToShortCode) {
				xeroShortCode = integrationSettings?.xeroTenantToShortCode[xeroTenantId];
			}

			buttons.push(
				<ForecastTooltip content={intl.formatMessage({id: 'invoicing.open_invoice_in'}, {systemName: 'Xero'})} delay={[50, 100]}>
					<XeroIcon
						onClick={() => {
							if (xeroShortCode) {
								window.open(`https://go.xero.com/organisationlogin/default.aspx?shortcode=${xeroShortCode}&redirecturl=/AccountsReceivable/Edit.aspx?InvoiceID=${xeroId}`, '_blank');
							} else {
								window.open(`https://go.xero.com/AccountsReceivable/Edit.aspx?InvoiceID=${xeroId}`, '_blank');
							}
						}}
					/>
				</ForecastTooltip>
			);
		} else if (quickbooksId) {
			buttons.push(
				<ForecastTooltip content={intl.formatMessage({id: 'invoicing.open_invoice_in'}, {systemName: 'QuickBooks'})} delay={[50, 100]}>
					<QuickbooksIcon
						onClick={() => {
							window.open(`${process.env.QUICKBOOKS_BASE_URL}/app/invoice?deeplinkcompanyid=${quickbooksRealmId}&txnId=${quickbooksId}`, '_blank');
						}}
						size={QuickbooksIcon.SIZE.STANDARD}
					/>
				</ForecastTooltip>
			);
		} else if (economicId) {
			buttons.push(
				<ForecastTooltip content={intl.formatMessage({id: 'invoicing.open_invoice_in'}, {systemName: 'E-conomic'})} delay={[50, 100]}>
					<EconomicIcon
						onClick={() => {
							if (status === 'DRAFT') {
								window.open(`https://secure.e-conomic.com/sales/invoicing/invoices`, '_blank');
							} else if (status === 'APPROVED') {
								window.open(`https://secure.e-conomic.com/sales/invoicing/archive`, '_blank');
							} else if (status === 'SENT') {
								window.open(`https://secure.e-conomic.com/sales/invoicing/sent`, '_blank');
							}
						}}
						size={EconomicIcon.SIZE.STANDARD}
					/>
				</ForecastTooltip>
			);
		}
		if (buttons.length === 0) {
			buttons.push(
				<span style={{marginLeft: '8px'}}>
					<Button variant={Button.VARIANT.PURPLE_OUTLINE} size={Button.SIZE.STANDARD} onClick={() => onExportClick(invoiceId, xeroId, quickbooksId, economicId)}>
						{intl.formatMessage({id: 'common.export'})}
					</Button>
				</span>
			);
		}
		return buttons;
	};

	const changeStatus = invoiceId => selectedValue => {
		if (selectedValue && selectedValue.length === 1) {
			Util.CommitMutation(UpdateInvoiceMutation, {
				id: invoiceId,
				status: selectedValue[0],
			});
		}
	};

	return (
		<Table>
			<Table.Header>
				<Table.HeaderColumn usePadding align="left" visible width="EXTRA_SMALL">
					<Table.HeaderColumn.Title>{intl.formatMessage({id: 'common.id'})}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn flex={1} usePadding align="left" visible width="LARGE">
					<Table.HeaderColumn.Title>{intl.formatMessage({id: 'project_section.invoice_name'})}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="left" visible={boolEyeOptions.client} width="LARGE">
					<Table.HeaderColumn.Title>{intl.formatMessage({id: 'common.client'})}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="left" visible={boolEyeOptions.projects} width="LARGE">
					<Table.HeaderColumn.Title>{intl.formatMessage({id: 'common.projects'})}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="left" visible={boolEyeOptions.status} width="MEDIUM">
					<Table.HeaderColumn.Title>{intl.formatMessage({id: 'common.status'})}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="right" visible={boolEyeOptions.invoiceDate} width="MEDIUM">
					<Table.HeaderColumn.Title>{intl.formatMessage({id: 'invoicing.invoice_date'})}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="right" visible={boolEyeOptions.total_invoiced} width="MEDIUM">
					<Table.HeaderColumn.Title>
						<ForecastTooltip delay={[50, 100]} content={intl.formatMessage({id: 'invoicing.invoiced_tooltip'})}>
							{intl.formatMessage({id: 'common.total_invoiced'})}
						</ForecastTooltip>
					</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="right" visible={boolEyeOptions.paid} width="MEDIUM">
					<Table.HeaderColumn.Title>
						<ForecastTooltip delay={[50, 100]} content={intl.formatMessage({id: 'invoicing.paid_tooltip'})}>
							{intl.formatMessage({id: 'settings_subscription.paid'})}
						</ForecastTooltip>
					</Table.HeaderColumn.Title>
				</Table.HeaderColumn>

				<Table.HeaderColumn usePadding align="right" visible={boolEyeOptions.unpaid} width="MEDIUM">
					<Table.HeaderColumn.Title>
						<ForecastTooltip delay={[50, 100]} content={intl.formatMessage({id: 'invoicing.unpaid_tooltip'})}>
							{intl.formatMessage({id: 'settings_subscription.unpaid'})}
						</ForecastTooltip>
					</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="right" visible={boolEyeOptions.dueDate} width="MEDIUM">
					<Table.HeaderColumn.Title>{intl.formatMessage({id: 'common.due_date'})}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align={'left'} visible={boolEyeOptions.export} width={'SMALL'}>
					<Table.HeaderColumn.Title>{intl.formatMessage({id: 'common.export'})}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align={'center'} visible={boolEyeOptions.integrationLink} width={'MEDIUM'}>
					{/* integration button */}
				</Table.HeaderColumn>
				<Table.HeaderColumn align={'center'} visible={true} width={'EXTRA_SMALL'}>
					{/* context  menu*/}
				</Table.HeaderColumn>
			</Table.Header>
			<Table.Rows data={headerData} canExpand={false} hasHeader>
				{({rowData, tableColumnsProps}) => {
					return (
						<Table.Row {...tableColumnsProps}>
							<Table.Column />
							<Table.Column>{intl.formatMessage({id: 'common.totals'})}</Table.Column>
							<Table.Column />
							<Table.Column />
							<Table.Column />
							<Table.Column />
							<Table.Column>
								<Table.Column.Cell>{Util.getFormattedNumberWithCurrency(Util.GetCurrencySymbol(companyCurrency), rowData.totalInvoiced, intl)}</Table.Column.Cell>
							</Table.Column>
							<Table.Column>
								<Table.Column.Cell>{Util.getFormattedNumberWithCurrency(Util.GetCurrencySymbol(companyCurrency), rowData.totalPaid, intl)}</Table.Column.Cell>
							</Table.Column>
							<Table.Column>
								<Table.Column.Cell>{Util.getFormattedNumberWithCurrency(Util.GetCurrencySymbol(companyCurrency), rowData.totalUnpaid, intl)}</Table.Column.Cell>
							</Table.Column>
							<Table.Column />
							<Table.Column />
							<Table.Column>{/* integration button */}</Table.Column>
							<Table.Column>{/* context  menu*/}</Table.Column>
						</Table.Row>
					);
				}}
			</Table.Rows>
			<Table.Rows data={tableData} canExpand={false}>
				{({rowData, tableColumnsProps}) => {
					return (
						<Table.Row {...tableColumnsProps}>
							<Table.Column>
								<Table.Column.Cell onClick={() => onEditInvoice(rowData.companyInvoiceId)}>{rowData.companyInvoiceId}</Table.Column.Cell>
							</Table.Column>
							<Table.Column>
								<Table.Column.Cell onClick={() => onEditInvoice(rowData.companyInvoiceId)}>{rowData.name}</Table.Column.Cell>
							</Table.Column>
							<Table.Column>{rowData?.client?.name}</Table.Column>
							<Table.Column>
								<ProjectIndicatorList visibleCount={3}>
									{rowData.projects.map((project, index) => (
										<ForecastTooltip placement={'bottom'} content={<ProjectNameTooltip projects={rowData.projects} />} >
											<Link key={index} style={{textDecoration: 'none'}} to={`/project/P-${project.companyProjectId}`}>
												<ProjectIndicator disableLink color={project.projectColor} id={project.companyProjectId} noTitle={true} />
											</Link>
										</ForecastTooltip>
									))}
								</ProjectIndicatorList>
							</Table.Column>
							<Table.Column userpilot={'status-dropdown'}>
								<Dropdown
									size="large"
									disabled={rowData.isExported}
									usePortal
									onSelect={changeStatus(rowData.id)}
									dropdownAlignment={'center'}
									maxHeight={80}
									width={128}
									name={intl.formatMessage({id: 'common.status'})}
									selectedItems={[rowData.status]}
								>
									<Dropdown.SingleText value="DRAFT" searchString={intl.formatMessage({id: 'common.draft'})}>
										{intl.formatMessage({id: 'common.draft'})}
									</Dropdown.SingleText>
									<Dropdown.SingleText value="APPROVED" searchString={intl.formatMessage({id: 'common.approved'})}>
										{intl.formatMessage({id: 'common.approved'})}
									</Dropdown.SingleText>
									<Dropdown.SingleText value="SENT" searchString={intl.formatMessage({id: 'common.sent'})}>
										{intl.formatMessage({id: 'common.sent'})}
									</Dropdown.SingleText>
								</Dropdown>
							</Table.Column>
							<Table.Column>
								<Table.Column.Cell>{rowData.invoiceDate.format('D MMM YYYY')}</Table.Column.Cell>
							</Table.Column>
							<Table.Column>
								<Table.Column.Cell>{Util.getFormattedNumberWithCurrency(Util.GetCurrencySymbol(rowData.currency), rowData.invoicedWithoutTax, intl)}</Table.Column.Cell>
							</Table.Column>
							<Table.Column>
								<Table.Column.Cell>{Util.getFormattedNumberWithCurrency(Util.GetCurrencySymbol(rowData.currency), rowData.paidWithoutTax, intl)}</Table.Column.Cell>
							</Table.Column>
							<Table.Column>
								<Table.Column.Cell>{Util.getFormattedNumberWithCurrency(Util.GetCurrencySymbol(rowData.currency), rowData.invoicedWithoutTax - rowData.paidWithoutTax, intl)}</Table.Column.Cell>
							</Table.Column>
							<Table.Column>
								<Table.Column.Cell>{rowData.dueDate.format('D MMM YYYY')}</Table.Column.Cell>
							</Table.Column>
							<Table.Column />
							<Table.Column>
								<div style={{display: 'flex', alignItems: 'center'}}>{getIntegrationsContent(rowData.id, rowData.status, rowData.xeroId, rowData.xeroTenantId, rowData.economicId, rowData.quickbooksId, rowData.quickbooksRealmId)}</div>
							</Table.Column>
							<Table.Column>
								<KebabMenuIcon onClick={e => handleOpenContextMenu(e, rowData.id, rowData.companyInvoiceId)} cy={'invoice-row-context-menu'} userpilot={tableColumnsProps.index === 0 ? 'context-menu-wrapper-invoices' : null} />
							</Table.Column>
						</Table.Row>
					);
				}}
			</Table.Rows>
		</Table>
	);
};

InvoicesTable.propTypes = {
	integrationSettings: PropTypes.shape({
		xeroShortCode: PropTypes.string,
	}),
};
